import type { Dispatch, SetStateAction } from 'react';
import type { ActiveDocumentInfo } from '../../../shared/hooks/useActiveDocumentName';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import type { useThemeContext } from '../../../shared/providers/ThemeContext';
import type { MappingWorkspaceTabIndex } from '../lib/mappingWorkspacePageModel';
import type { MappingConfigurationPageContentProps } from './mappingWorkspaceContentModel.types';
import { useMappingHostRibbonBridge } from './useMappingHostRibbonBridge';
import { useMappingWorkspaceDiagnosticsWiring } from './useMappingWorkspaceDiagnosticsWiring';
import type { MappingWorkspaceNavigationApi } from './useMappingWorkspaceNavigation';
import type { MappingWorkspaceFocusRefs } from './useMappingWorkspaceFocusRefs';
import type { useMappingWorkspaceProfileRuntime } from './useMappingWorkspaceProfileRuntime';

type ConfigurationState = ReturnType<typeof useConfigurationStore.getState>;
type ThemeContext = ReturnType<typeof useThemeContext>;
type ProfileRuntime = ReturnType<typeof useMappingWorkspaceProfileRuntime>;

interface UseMappingWorkspaceChromeRuntimeArgs {
  props: MappingConfigurationPageContentProps;
  profileRuntime: ProfileRuntime;
  processConfig: ConfigurationState['processPropertiesConfiguration'];
  activeDocumentInfo: ActiveDocumentInfo;
  focusRefs: MappingWorkspaceFocusRefs;
  activeTab: MappingWorkspaceTabIndex;
  isQuickFillWorkspaceTab: boolean;
  requestWorkspaceTabChange: MappingWorkspaceNavigationApi['requestWorkspaceTabChange'];
  currentSettingsNeedProfileSave: boolean;
  quickFillHasPendingChanges: boolean;
  showMappingWorkspaceSummary: boolean;
  user: { username: string } | null;
  isSigningOut: boolean;
  handleOpenActivePageGuide: () => void;
  handleOpenActivePageTour: () => void;
  handleOpenSummaryWindow: () => void;
  handleSignOut: () => Promise<void>;
  themeContext: ThemeContext;
  setHostProfileSaveAsDialogOpen: Dispatch<SetStateAction<boolean>>;
  setHostProfileRenameDialogProfileKey: Dispatch<SetStateAction<string | null>>;
}

export const useMappingWorkspaceChromeRuntime = ({
  props,
  profileRuntime,
  processConfig,
  activeDocumentInfo,
  focusRefs,
  activeTab,
  isQuickFillWorkspaceTab,
  requestWorkspaceTabChange,
  currentSettingsNeedProfileSave,
  quickFillHasPendingChanges,
  showMappingWorkspaceSummary,
  user,
  isSigningOut,
  handleOpenActivePageGuide,
  handleOpenActivePageTour,
  handleOpenSummaryWindow,
  handleSignOut,
  themeContext,
  setHostProfileSaveAsDialogOpen,
  setHostProfileRenameDialogProfileKey,
}: UseMappingWorkspaceChromeRuntimeArgs) => {
  const { gate, repair, profileLoad, progress, hasResolvedSelectedProfile } = profileRuntime;
  const {
    projectIdentityPending,
    selectedProfileKey,
    profiles,
    profilesLoading,
    isSaving,
    onSelectedSettingsTypeChange,
    onRefreshProfiles,
    onSaveProfile,
    onDeleteProfile,
    onDeleteProfiles,
    lastAppliedProfileKey,
    dockablePaneVisible,
    isLiveWizardMappingHandoff,
    projectPreferredProfile,
  } = props;

  const profileOperationActive = progress.profileApplyWindowOpen || repair.mappingReadiness.isRepairing;
  const profileActionsDisabled =
    projectIdentityPending || gate.projectProfileGateBlocksActions || profileOperationActive;
  const profileSelectionDisabled = projectIdentityPending || profileOperationActive;
  const profileLoadDisabled = projectIdentityPending || profileOperationActive;

  useMappingHostRibbonBridge({
    activeTab,
    requestWorkspaceTabChange,
    selectedProfileKey,
    profiles,
    profilesLoading,
    resolvedProfileName: gate.resolvedProfileName,
    resolvedSettingsType: gate.resolvedSettingsType,
    isSaving,
    currentSettingsNeedProfileSave,
    profileActionsDisabled,
    profileSelectionDisabled,
    profileLoadDisabled,
    showMappingWorkspaceSummary,
    user,
    isSigningOut,
    handleLoadProfileForCurrentProject: gate.handleLoadProfileForCurrentProject,
    confirmProfileLoad: profileLoad.confirmLoadSelectedProfile,
    lastAppliedProfileKey,
    handleWorkspaceSelectedProfileChange: gate.handleWorkspaceSelectedProfileChange,
    onSelectedSettingsTypeChange,
    onRefreshProfiles,
    onSaveProfile,
    onDeleteProfile,
    onDeleteProfiles,
    handleOpenActivePageGuide,
    handleOpenActivePageTour,
    handleOpenSummaryWindow,
    handleSignOut,
    themeContext,
    setHostProfileSaveAsDialogOpen,
    setHostProfileRenameDialogProfileKey,
  });

  const quickFillCanApplyWhileMappingBlocked =
    isQuickFillWorkspaceTab && repair.mappingReadiness.isBlocking && quickFillHasPendingChanges;
  const mappingReadinessBlocksWorkspaceApply =
    repair.mappingReadiness.isBlocking && !quickFillCanApplyWhileMappingBlocked;
  const workspaceApplyDisabled =
    isSaving || projectIdentityPending || mappingReadinessBlocksWorkspaceApply || gate.projectProfileGateBlocksActions;
  const workspaceContinueDisabled =
    isSaving || projectIdentityPending || repair.mappingReadiness.isBlocking || gate.projectProfileGateBlocksActions;
  const workspaceApplyNeedsAttention =
    !workspaceApplyDisabled &&
    (quickFillHasPendingChanges || (!repair.mappingReadiness.isBlocking && currentSettingsNeedProfileSave));

  useMappingWorkspaceDiagnosticsWiring({
    processConfig,
    activeDocumentInfo,
    gate,
    mappingReadiness: repair.mappingReadiness,
    repairConsentGate: {
      mappingReadinessEnabled: repair.mappingReadinessEnabled,
      profileAutoRepairAllowed: repair.profileAutoRepairAllowed,
      profileAutoRepairBlockedMessage: repair.profileAutoRepairBlockedMessage,
      confirmedRepairProfileKey: repair.confirmedRepairProfileKey,
      resolvedProfileMissingPreview: repair.resolvedProfileMissingPreview,
    },
    flags: {
      dockablePaneVisible,
      isLiveWizardMappingHandoff,
      hasResolvedSelectedProfile,
      resolvedProfileName: gate.resolvedProfileName,
      profilesLoading,
      lastAppliedProfileKey,
      selectedProfileKey,
      projectIdentityPending,
      projectPreferredProfileKey: projectPreferredProfile?.profileKey ?? null,
      activeTab,
    },
    applyAttention: {
      activeTab,
      isQuickFillWorkspaceTab,
      workspaceApplyDisabled,
      workspaceContinueDisabled,
      workspaceApplyNeedsAttention,
      quickFillHasPendingChanges,
      quickFillCanApplyWhileMappingBlocked,
      currentSettingsNeedProfileSave,
      isSaving,
      mappingReadinessBlocksWorkspaceApply,
      projectProfileGateBlocksActions: gate.projectProfileGateBlocksActions,
      workspaceApplyAttentionTraceKey: focusRefs.workspaceApplyAttentionTraceKey,
    },
  });

  return {
    profileActionsDisabled,
    profileSelectionDisabled,
    profileLoadDisabled,
    workspaceApplyDisabled,
    workspaceContinueDisabled,
    workspaceApplyNeedsAttention,
  };
};
